
import React from 'react';
import { RichTextRenderer } from '@/components/blog/RichTextRenderer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, User, Linkedin } from 'lucide-react';
import { BlogAuthor } from '@/components/blog/BlogAuthor';

interface BlogPreviewProps {
  title: string;
  content: string;
  coverImage?: string;
  date: string;
  author?: string;
  authorAvatar?: string;
  authorBio?: string;
  authorLinkedIn?: string;
}

export const BlogPreview: React.FC<BlogPreviewProps> = ({
  title,
  content,
  coverImage,
  date,
  author = 'edgefi team',
  authorAvatar,
  authorBio,
  authorLinkedIn
}) => {
  return (
    <Card className="shadow-md">
      <CardHeader className="bg-[#F7F7F6] border-b">
        <CardTitle className="text-lg font-medium">Preview</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {coverImage && (
          <div className="relative w-full h-48">
            <img 
              src={coverImage} 
              alt={title} 
              className="w-full h-full object-cover" 
            />
          </div>
        )}
        
        <div className="p-6 space-y-4">
          <h1 className="text-2xl font-bold text-[#272727]">{title || 'Untitled Post'}</h1>
          
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Calendar className="mr-1 h-4 w-4" />
              <span>{date}</span>
            </div>
            <div className="flex items-center">
              <Clock className="mr-1 h-4 w-4" />
              <span>5 min read</span>
            </div>
          </div>
          
          {author && (
            <div className="py-4 border-t border-b">
              <BlogAuthor 
                name={author} 
                avatarUrl={authorAvatar}
                bio={authorBio}
                role=""
                compact={true}
              />
            </div>
          )}
          
          <div className="pt-4">
            <RichTextRenderer 
              content={content} 
              className="prose prose-sm max-w-none blog-content"
            />
          </div>

          {author && authorBio && (
            <div className="mt-8 p-5 bg-gray-50 rounded-xl border border-gray-100">
              <h3 className="text-lg font-bold mb-1">About the Author</h3>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                <div className="shrink-0">
                  {authorAvatar ? (
                    <img 
                      src={authorAvatar} 
                      alt={author}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-edgefi-amethyst/20 text-edgefi-purple flex items-center justify-center text-xl font-bold">
                      {author[0]}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="text-base font-semibold">{author}</h4>
                  <p className="text-sm text-gray-600 mt-1">{authorBio}</p>
                  {authorLinkedIn && (
                    <a 
                      href={authorLinkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 flex items-center mt-2 hover:underline"
                    >
                      <Linkedin className="h-4 w-4 mr-1" />
                      Connect on LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
